interface DebounceOptions {
  /** If `true`, the function will be invoked on the leading edge of the delay interval. */
  leading?: boolean;
}

/**
 * Creates a debounced function that delays invoking the provided function until after the specified delay has elapsed
 * since the last time the debounced function was invoked. Optionally, the function can be invoked on the leading edge
 * of the delay interval.
 *
 * @param func - The function to debounce.
 * @param delay - The number of milliseconds to delay; default is 300 milliseconds.
 * @param options - The options object.
 * @returns A new debounced function with a `cancel` method to cancel any pending invocation.
 */
export const debounce = <T extends unknown[], R = void>(
  func: (...args: T) => R,
  delay = 300,
  { leading = false }: DebounceOptions = {}
) => {
  let timerId: NodeJS.Timeout | undefined;
  let shouldInvoke = false;

  const debounced = (...args: T) => {
    shouldInvoke = true;

    if (!timerId && leading) {
      func(...args);
      shouldInvoke = false;
    }
    clearTimeout(timerId);

    timerId = setTimeout(() => {
      if (shouldInvoke) {
        func(...args);
      }
    }, delay);
  };

  debounced.cancel = () => {
    clearTimeout(timerId);
    timerId = undefined;
  };

  return debounced;
};
